import { Pair, Route, TokenAmount, Trade, TradeType } from '@uniswap/sdk';
import type { ReactElement } from 'react';
import React, { useCallback, useMemo, useState } from 'react';

import { useWeb3State } from '../../../hooks';
import { useStarsEthLpTokenContract } from '../../../hooks/lp-token-contract.hooks';
import { WEB3_CONFIG } from '../../../lib';
import type { walletClientToSigner } from '../../../lib/smart-contract-utils';
import { parseEther } from '../../../lib/smart-contract-utils';
import {
  getPancakeTradeMinimumAmountOut,
  swapExactUsdtForMogul,
} from '../../../lib/uniswap/uniswap';
import {
  BUSDT,
  defaultPairBinance,
  defaultRouteBinance,
  defaultTradeBinance,
  STARSBINANCE,
  TradeStateContext,
} from './trade.context';

interface Props {
  children: ReactElement;
}

// export const slippageTolerance = new Percent('50', '10000'); // 50 bips, or 0.50%
// export const slippageTolerance = '50'; // 50 bips, or 0.50%

export const TradeStateContextProvider: React.FC<Props> = ({ children }) => {
  const { walletDetails } = useWeb3State();
  const [initialized, setInitialized] = useState(false);
  const [ethValue, setEthValue] = useState(parseEther('100').toString());
  const [pair, setPair] = useState<Pair>(defaultPairBinance);
  const [route, setRoute] = useState<Route>(defaultRouteBinance);
  const [trade, setTrade] = useState<Trade>(defaultTradeBinance);
  const starsBnbContract = useStarsEthLpTokenContract(
    false,
    walletDetails.chain.id === WEB3_CONFIG.chains.bsc.id,
  );

  const updateTrade = useCallback(
    (newValue: string) => {
      const newTrade = new Trade(
        route,
        new TokenAmount(BUSDT, newValue),
        TradeType.EXACT_INPUT,
      );

      setEthValue(newValue);
      setTrade(newTrade);
    },
    [route],
  );

  const executeTrade = useCallback(
    async (
      signer: ReturnType<typeof walletClientToSigner>,
      recipient: string,
      slippageTolerance: string,
    ) => {
      const minAmountOut = await getPancakeTradeMinimumAmountOut(
        trade,
        slippageTolerance,
      );

      const amountOutMin = minAmountOut.toFixed(0);
      const deadline = Math.floor(Date.now() / 1000) + 60 * 20; // 20 minutes from the current Unix time
      const value = trade.inputAmount.raw.toString();

      return swapExactUsdtForMogul(signer, {
        amountOutMin,
        recipient,
        deadline,
        value,
        currentNetwork: walletDetails.chain.type,
      });
    },
    [trade.inputAmount.raw, walletDetails.chain.type],
  );

  // initialize trade
  useMemo(() => {
    void (async () => {
      const isEth = walletDetails.chain.id === WEB3_CONFIG.chains.eth.id;

      if (isEth) {
        // const retrievedPair = await Fetcher.fetchPairData(
        //   STARS,
        //   WETH[STARS.chainId],
        // );
        // const tradeToExecute = new Trade(
        //   defaultRoute,
        //   new TokenAmount(WETH[STARS.chainId], parseEther('0.1').toString()),
        //   TradeType.EXACT_INPUT,
        // );
        // setPair(retrievedPair);
        // setRoute(new Route([retrievedPair], WETH[STARS.chainId]));
        // setTrade(tradeToExecute);
      } else {
        const reserves = await starsBnbContract.getStarsEthReserves(); // todo: change
        console.log('reserves', reserves);

        if (reserves.length !== 3) {
          return;
        }

        const retrievedPair = new Pair(
          new TokenAmount(BUSDT, reserves[0].toString()),
          new TokenAmount(STARSBINANCE, reserves[1].toString()),
        );

        const tradeToExecute = new Trade(
          defaultRouteBinance,
          new TokenAmount(BUSDT, parseEther('100').toString()),
          TradeType.EXACT_INPUT,
        );

        setPair(retrievedPair);
        setRoute(new Route([retrievedPair], BUSDT));
        setTrade(tradeToExecute);
      }
      setInitialized(true);
    })();
  }, []);

  const contextState = useMemo(
    () => ({
      initialized,
      value: ethValue,
      pair,
      trade,
      setValue: setEthValue,
      updateTrade,
      executeTrade,
    }),
    [initialized, ethValue, pair, trade, updateTrade, executeTrade],
  );

  return (
    <TradeStateContext.Provider value={contextState}>
      {children}
    </TradeStateContext.Provider>
  );
};
